import { formatPrice } from "@/utils/price";

/**
 * 通用价格单位转换方法
 * @param {number|string} price - 原始价格
 * @param {number} fromUnit - 原始价格单位 (10:美元, 0:人民币)
 * @param {number} toUnit - 目标价格单位 (10:美元, 0:人民币)
 * @param {object} priceConfig - 价格配置对象，包含汇率信息
 * @returns {number} 转换后的价格
 */
export function convertPriceByUnit(price, fromUnit, toUnit, priceConfig = {}) {
  if (!price || isNaN(Number(price))) {
    return 0;
  }

  const priceNum = Number(price);
  const { usdUnit, cnyUnit } = priceConfig;
  // 默认汇率配置
  const defaultRate = 7.17;
  const rate = usdUnit && usdUnit.rate ? Number(usdUnit.rate) : defaultRate;

  // 动态获取单位值
  const usdValue =
    usdUnit && usdUnit.value !== undefined ? Number(usdUnit.value) : 10;
  const cnyValue =
    cnyUnit && cnyUnit.value !== undefined ? Number(cnyUnit.value) : 0;

  if (fromUnit === toUnit) {
    return Number(formatPrice(priceNum));
  }

  // 使用动态值进行判断
  if (fromUnit === usdValue && toUnit === cnyValue) {
    return Number(formatPrice(priceNum * rate));
  } else if (fromUnit === cnyValue && toUnit === usdValue) {
    return Number(formatPrice(priceNum / rate));
  }

  return Number(formatPrice(priceNum));
}
/**
 * 获取转换后的单位名称
 * 提示语言显示的单位名称：1美元等于（7.17人民币）-这里是转化后的人名币单位
 * @param {number} currentUnit - 当前价格单位
 * @param {object} priceConfig - 价格配置对象
 * @returns {string} 转换后的单位名称
 */
export function getConvertedUnitName(currentUnit, priceConfig = {}) {
  const { usdUnit, cnyUnit } = priceConfig;

  if (!usdUnit || !cnyUnit) {
    return currentUnit == 10 ? "人民币" : "美元";
  }

  const usdValue = Number(usdUnit.value != null ? usdUnit.value : 10);
  return currentUnit == usdValue ? cnyUnit.name : usdUnit.name;
}

/**
 * 根据价格单位值获取单位名称
 * 10-USD, 0-CNY
 * @param {number|string} currentUnit - 价格单位值；传入全局单位树时也支持币种编码
 * @param {object} priceConfig - usdUnit、cnyUnit；可选 unitTree 为全局价格单位树
 * @returns {string} 价格单位名称
 */
export function getPriceUnitName(currentUnit, priceConfig = {}) {
  const { usdUnit, cnyUnit } = priceConfig;

  // 显式传入全局单位树时按真实配置匹配，不将其他币种或未加载状态当成人民币。
  // 未传入单位树的既有调用仍沿用原来的双币种规则。
  if (Object.prototype.hasOwnProperty.call(priceConfig, "unitTree")) {
    if (currentUnit === undefined || currentUnit === null || String(currentUnit).trim() === "") {
      return "";
    }
    const value = String(currentUnit).trim().toUpperCase();
    const units = [
      ...(Array.isArray(priceConfig.unitTree?.children) ? priceConfig.unitTree.children : []),
      usdUnit,
      cnyUnit,
    ].filter(Boolean);
    const unit = units.find((item) =>
      [item.value, item.number].some(
        (candidate) =>
          candidate !== undefined &&
          candidate !== null &&
          String(candidate).trim().toUpperCase() === value
      )
    );
    return unit?.name || "";
  }

  if (!usdUnit || !cnyUnit) {
    return currentUnit == 10 ? "美元" : "人民币";
  }

  const usdValue = Number(usdUnit.value != null ? usdUnit.value : 10);

  // 检查 usdUnit.name 和 cnyUnit.name 是否存在
  const usdName = usdUnit.name || "美元";
  const cnyName = cnyUnit.name || "人民币";

  return currentUnit == usdValue ? usdName : cnyName;
}
